/**
 * Engines the driver layer can talk to. MySQL is implemented; the other two
 * are declared now so that every type, route and UI affordance that fans out
 * over engines is written multi-engine from the start rather than retrofitted.
 */
export const ENGINES = ['MYSQL', 'POSTGRES', 'MONGODB'] as const;
export type Engine = (typeof ENGINES)[number];

/**
 * What each engine's driver can actually do. The console reads these to decide
 * which affordances to render — e.g. no "Explain" button for an engine whose
 * driver reports supportsExplain: false — instead of hardcoding `if MYSQL`.
 */
export interface EngineCapabilities {
  /** The query language shown in the editor; drives Monaco's syntax mode. */
  language: 'sql' | 'mongo-find';
  supportsExplain: boolean;
  /** Can a running statement be killed server-side? */
  supportsCancel: boolean;
  /** Can results be streamed row-by-row rather than buffered whole? */
  supportsStreaming: boolean;
  /** Does the engine namespace tables under schemas separate from databases? */
  supportsSchemas: boolean;
  supportsTransactionReadOnly: boolean;
}

export const ENGINE_CAPABILITIES: Record<Engine, EngineCapabilities> = {
  MYSQL: {
    language: 'sql',
    supportsExplain: true,
    supportsCancel: true,
    supportsStreaming: true,
    supportsSchemas: false,
    supportsTransactionReadOnly: true,
  },
  POSTGRES: {
    language: 'sql',
    supportsExplain: true,
    supportsCancel: true,
    supportsStreaming: true,
    supportsSchemas: true,
    supportsTransactionReadOnly: true,
  },
  MONGODB: {
    language: 'mongo-find',
    supportsExplain: true,
    supportsCancel: true,
    supportsStreaming: true,
    supportsSchemas: false,
    supportsTransactionReadOnly: false,
  },
};

/** A datasource as exposed to the client. Credentials never appear here. */
export interface DataSourceSummary {
  id: string;
  key: string;
  name: string;
  engine: Engine;
  /** Logical database/keyspace the connection defaults to. */
  database: string;
  description: string | null;
  enabled: boolean;
  capabilities: EngineCapabilities;
  limits: {
    maxRows: number;
    timeoutMs: number;
  };
  /** Populated by an admin-only health probe, not on every list call. */
  health?: {
    reachable: boolean;
    latencyMs: number | null;
    checkedAt: string;
    error: string | null;
  };
}

/** Admin-facing write payload. `password` is write-only and never read back. */
/**
 * The connection settings an administrator needs in order to *edit* a
 * connection, which the summary deliberately leaves out.
 *
 * Host, port and username are infrastructure, not credentials — but they still
 * describe where the company's data lives, so they are handed only to someone
 * who already holds `datasource:write`. The password is absent by construction:
 * there is no shape of this type that can carry it, so no future edit here can
 * leak one by accident.
 */
export interface DataSourceConfig {
  id: string;
  host: string;
  port: number;
  username: string;
  ssl: boolean;
}

export interface DataSourceUpsertInput {
  key: string;
  name: string;
  engine: Engine;
  host: string;
  port: number;
  database: string;
  username: string;
  password?: string;
  description?: string | null;
  ssl?: boolean;
  enabled?: boolean;
  maxRows?: number;
  timeoutMs?: number;
}
